import type {ReactNode} from 'react';
import React from 'react';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import type {MobilePageSelectorPageProps} from './mobile-page-selector-page.types';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {Text, TextSize, TextType} from '@Components/text';
import styles from './mobile-page-selector-page.module.scss';
import {motion} from 'framer-motion';
import {useAppSelector} from '@/hooks';
import {MobilePageSelectionModeState} from '@Components/poster-editor/poster-editor.types';
import {exitMobilePageSelectionMode} from '@Components/poster-editor/library/poster-editor-library';

export function MobilePageSelectorPage({isDragOverlay = false, ...props}: MobilePageSelectorPageProps): ReactNode {
  const preview = useAppSelector((state) => {
    return state.posterEditor.pagePreviews[props.pageHashedId];
  });

  const mobilePageSelectionModeState = useAppSelector((state) => {
    return state.posterEditor.mobilePageSelectionModeState;
  });

  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: props.pageHashedId,
    disabled: isDragOverlay,
  });

  const getSkeletonLoader = (): ReactNode => {
    return <SkeletonBrick width={100} height={100} isPulsating={false} radius={'radius-4'} />;
  };

  const getPagePreviewElement = (): ReactNode => {
    if (!preview) {
      return getSkeletonLoader();
    }

    return (
      <div className={`flexbox flex-center _full-width _full-height radius-4 bg-neutral-6 ${styles.imageContainer}`}>
        <img className={styles.image} src={preview} alt={`Page ${props.pageNumber} preview`} />
      </div>
    );
  };

  const handleClick = (): void => {
    if (isDragOverlay) {
      return;
    }

    if (props.isSelected) {
      exitMobilePageSelectionMode();
      return;
    }

    window.posterEditor?.whiteboard?.setCurrentPage(props.pageHashedId);
  };

  const getMobilePageSelectorPageComponent = (): ReactNode => {
    if (mobilePageSelectionModeState === MobilePageSelectionModeState.TRANSITIONING) {
      return <SkeletonBrick isPulsating={false} height={130} />;
    }

    return (
      <motion.div
        layout
        initial={{opacity: 1}}
        className={`${styles.pageItem} _full-width _full-height spacing-p-1 flex-v-row flex-center gap-1 ${props.isSelected ? styles.selected : ''} ${isDragOverlay ? styles.dragOverlay : ''} ${isDragging ? '_invisible' : ''}`}
        ref={setNodeRef}
        style={{
          transform: CSS.Translate.toString(transform),
          transition,
        }}
        {...attributes}
        {...listeners}
        onClick={handleClick}
      >
        <motion.div
          className="_full-width _full-height flex-v-row flex-center gap-1"
          initial={props.isSelected && !isDragOverlay ? {scale: 1.5} : {scale: 1}}
          animate={{scale: 1}}
          transition={{duration: 0.3, ease: 'easeOut'}}
        >
          {getPagePreviewElement()}
          {isDragOverlay ? null : (
            <Text
              val={props.pageNumber.toString()}
              size={TextSize.XXSMALL}
              bold
              type={TextType.BODY}
              className={`${styles.pageNumber} ${props.isSelected ? styles.selected : ''}`}
            />
          )}
        </motion.div>
      </motion.div>
    );
  };

  return getMobilePageSelectorPageComponent();
}
